export type MatchmakingRequestState =
  | "draft"
  | "evaluating"
  | "awaiting_admission"
  | "paused"
  | "completed"
  | "cancelled"
  | "failed";

export type VenueMatchmakingEligibilityStatus =
  | "eligible"
  | "disabled"
  | "not_authorized"
  | "not_public"
  | "not_confirmed"
  | "already_started"
  | "full"
  | "missing_capacity"
  | "missing_sport"
  | "missing_match_context"
  | "existing_request"
  | "terminal_booking";

export interface RequestProjectionV1 {
  id: string;
  bookingId: string;
  state: MatchmakingRequestState;
  version: number;
  configVersion: string;
  updatedAt?: string;
  summary?: {
    candidateCount?: number;
    broadExplanation?: string;
  };
}

export interface VenueMatchmakingControl {
  bookingId: string;
  canTrigger: boolean;
  status: VenueMatchmakingEligibilityStatus;
  openPlaces: number;
  request: RequestProjectionV1 | null;
}

export interface VenueMatchmakingControlEnvelope {
  item: VenueMatchmakingControl;
}

export interface RequestProjectionEnvelope {
  item: RequestProjectionV1;
}

export interface VenueMatchmakingUiError {
  code: string;
  message: string;
  retryable: boolean;
  reconcile: boolean;
  retryAfterSeconds?: number;
}

const REQUEST_STATE_LABELS: Record<MatchmakingRequestState, string> = {
  draft: "Match Making queued",
  evaluating: "Finding eligible players",
  awaiting_admission: "Invitations in progress",
  paused: "Match Making paused",
  completed: "Match Making completed",
  cancelled: "Match Making cancelled",
  failed: "Match Making needs attention",
};

const CONTROL_STATUS_MESSAGES: Record<
  Exclude<VenueMatchmakingEligibilityStatus, "eligible">,
  string
> = {
  disabled: "Match Making is not available for this booking.",
  not_authorized:
    "You do not have permission to manage Match Making for this booking.",
  not_public: "Make this booking public to use Match Making.",
  not_confirmed: "Confirm this booking to use Match Making.",
  already_started: "This booking has already started.",
  full: "This booking is already full.",
  missing_capacity: "Set a valid player capacity to use Match Making.",
  missing_sport: "Set a supported sport to use Match Making.",
  missing_match_context:
    "Add match format, composition, and at least one confirmed player before starting Match Making.",
  existing_request: "A Match Making request already exists for this booking.",
  terminal_booking: "Match Making cannot start for a finished booking.",
};

export function requestStateLabel(state: MatchmakingRequestState): string {
  return REQUEST_STATE_LABELS[state];
}

export function controlStatusMessage(
  status: VenueMatchmakingEligibilityStatus,
): string {
  return status === "eligible" ? "" : CONTROL_STATUS_MESSAGES[status];
}

export function isTerminalRequest(state: MatchmakingRequestState): boolean {
  return state === "completed" || state === "cancelled" || state === "failed";
}
